import { useContext, useState } from "react";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { useHeaderMenu } from "@/features/header/hooks/useHeaderMenu";
import { Link, Menu, Button, Box } from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import { userDataContext } from "@/hooks/useUserData";
import { HEADER_HEIGHT } from "@/features/header";
import { Link as RouterLink } from "react-router-dom";

interface MenuItemConfig {
  label: string;
  icon?: React.ReactNode;
  /** 内部遷移のリンク先。指定した項目は <a> として描画する */
  to?: string;
  onClick?: () => void;
}

export const HeaderMenu: React.FC = () => {
  const { isVisible, handleClickLogout } = useHeaderMenu();
  const userData = useContext(userDataContext);
  const email = userData.data?.email || "";

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const menuItems: MenuItemConfig[] = [
    {
      label: "アカウント管理",
      to: "/account",
    },
    {
      label: "ログアウト",
      icon: <LogoutIcon fontSize="small" />,
      onClick: handleClickLogout,
    },
  ];

  if (!isVisible) return null;

  return (
    <>
      <Button
        id="account-button"
        aria-controls={isOpen ? "account-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={isOpen ? "true" : undefined}
        onClick={handleClick}
        sx={{
          color: "darkgray.main",
          textTransform: "none",
          fontWeight: 400,
          p: { xs: "8px", md: "6px 16px" },
          minWidth: "auto",
          gap: email ? 1 : 0,
          "&:hover": { backgroundColor: "background.default" },
        }}
      >
        <AccountCircleIcon />
        {email && (
          <Box
            component="span"
            sx={{
              display: { xs: "none", md: "block" },
            }}
          >
            {email}
          </Box>
        )}
      </Button>
      <Menu
        id="account-menu"
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "account-button",
          sx: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "24px 50px",
            gap: "37px",
          },
        }}
        slotProps={{
          paper: {
            sx: {
              // MUIのPopoverはJS計算によるインラインスタイルで位置を設定するため、
              // ヘッダー直下に固定表示するには!importantが必要
              position: "fixed",
              top: `${HEADER_HEIGHT}px !important`,
              marginTop: "-1px !important",
              right: "16px !important",
              left: "auto !important",
              background: "white.main",
              boxShadow: "0px 4px 8px -2px rgba(0, 0, 0, 0.25)",
              borderRadius: "0px 0px 8px 8px",
            },
          },
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {menuItems.map((item) => (
          <Link
            key={item.label}
            {...(item.to != null
              ? { component: RouterLink, to: item.to }
              : { component: "button" as const })}
            color="inherit"
            underline="none"
            onClick={() => {
              handleClose();
              item.onClick?.();
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              color: "darkgray.main",
              fontSize: "14px",
              "&:hover": {
                color: "primary.main",
              },
            }}
          >
            {item.icon}
            {item.label}
          </Link>
        ))}
      </Menu>
    </>
  );
};
